import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FiType, FiCode, FiLink, FiLink2 } from 'react-icons/fi';

/**
 * RichTextBody — dual-mode email body editor.
 *
 * "Text" mode (default): an <iframe> with designMode="on" and email-template-like
 *   CSS (Arial, 12px, navy headings, 680px document page width, centered) so the
 *   editor looks like the final email template. Pasting from Word/docs preserves
 *   formatting; Word's proprietary markup AND border styles (which cause the
 *   black pipe artifacts) are stripped on paste.
 *
 * "HTML" mode: a plain textarea showing the raw HTML source.
 *
 * Both modes read/write the same `value` prop (always HTML under the hood),
 * so sending, preview, templates, and {{variables}} all keep working.
 */

// ─── Default CSS injected into the iframe ─────────────────────────────────
// Matches the Vardaan email template look: 680px page width, Arial, navy headings.
const IFRAME_CSS = `
  body {
    font-family: Arial, Helvetica, sans-serif;
    font-size: 12px;
    color: #2D3748;
    line-height: 1.7;
    margin: 0;
    padding: 24px 16px;
    background: #F5F5F5;
  }
  /* Document page — centered, 680px max-width like the email template */
  .editor-page {
    max-width: 680px;
    margin: 0 auto;
    background: #FFFFFF;
    min-height: 500px;
    box-shadow: 0 1px 4px rgba(0,0,0,0.08);
  }
  /* Plain mode — no letterhead, just padded body area */
  .editor-page.editor-plain .editor-body {
    padding: 28px 32px;
    min-height: 400px;
  }
  /* Letterhead header — non-editable */
  .editor-header {
    padding: 0 32px;
  }
  .editor-header .rule-navy {
    border-top: 6px solid #0E2A4A;
    font-size: 1px;
    line-height: 1px;
    height: 1px;
  }
  .editor-header .rule-clay {
    border-top: 1px solid #B8624A;
    font-size: 1px;
    line-height: 1px;
    height: 1px;
  }
  .editor-header .spacer-sm { height: 3px; font-size: 1px; line-height: 1px; }
  .editor-header .spacer-md { height: 20px; font-size: 1px; line-height: 1px; }
  .editor-header .logo-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding-top: 4px;
  }
  .editor-header .logo-left img {
    display: block;
    max-width: 160px;
    height: auto;
  }
  .editor-header .logo-left .company-name {
    font-size: 11px;
    margin-top: 10px;
    font-weight: bold;
    letter-spacing: 1.2px;
    text-transform: uppercase;
    color: #0E2A4A;
  }
  .editor-header .links-right {
    text-align: right;
    font-size: 11px;
    line-height: 1.8;
  }
  .editor-header .links-right a {
    color: #0E2A4A;
    text-decoration: none;
  }
  .editor-header .office-band {
    border-top: 1px solid #C9BFA8;
    border-bottom: 1px solid #C9BFA8;
    padding: 10px 0;
    margin-top: 18px;
    text-align: center;
    font-size: 11px;
    letter-spacing: 0.5px;
    color: #3E4A5B;
  }
  /* Editable body area */
  .editor-body {
    padding: 28px 32px;
    min-height: 300px;
    outline: none;
  }
  /* Letterhead footer — non-editable */
  .editor-footer {
    padding: 0 32px;
  }
  .editor-footer .footer-confidential {
    border-top: 1px solid #C9BFA8;
    padding-top: 12px;
    font-size: 9px;
    color: #6B6258;
    line-height: 1.5;
    text-align: center;
  }
  .editor-footer .spacer-foot { height: 12px; font-size: 1px; line-height: 1px; }
  h1, h2, h3, h4, h5, h6 {
    color: #0E2A4A;
    font-family: Arial, Helvetica, sans-serif;
    line-height: 1.4;
    margin: 16px 0 8px 0;
  }
  h1 { font-size: 20px; font-weight: bold; }
  h2 { font-size: 16px; font-weight: bold; }
  h3 { font-size: 14px; font-weight: bold; }
  p { margin: 0 0 10px 0; }
  a { color: #0E2A4A; }
  ul, ol { margin: 8px 0 8px 24px; padding: 0; }
  li { margin-bottom: 4px; }
  table { border-collapse: collapse; max-width: 100%; }
  td, th { padding: 6px 8px; }
  blockquote {
    margin: 8px 0;
    padding-left: 12px;
    border-left: 3px solid #C9BFA8;
    color: #3E4A5B;
  }
  img { max-width: 100%; height: auto; }
  strong, b { font-weight: bold; }
  em, i { font-style: italic; }
  u { text-decoration: underline; }
  hr { border: 0; border-top: 1px solid #C9BFA8; margin: 16px 0; }
  /* Placeholder when empty */
  .editor-placeholder {
    color: #999;
    font-style: italic;
  }
`;

// ─── Letterhead header HTML (editable text, non-editable rules/logo) ─────
const LETTERHEAD_HEADER = `
<div class="editor-header">
  <div class="rule-navy" contenteditable="false"></div>
  <div class="spacer-sm" contenteditable="false"></div>
  <div class="rule-clay" contenteditable="false"></div>
  <div class="spacer-md" contenteditable="false"></div>
  <div class="logo-row">
    <div class="logo-left">
      <img src="https://innoida.utho.io/bucket-8iy6qayq/bulk-mail-logos/images/Vardaanlogo.png" width="160" alt="Vardaan Software Services Limited" contenteditable="false" />
      <div class="company-name" contenteditable="true">Software Services Limited</div>
    </div>
    <div class="links-right" contenteditable="true">
      <div><a href="https://vardaanglobal.uk">www.vardaanglobal.uk</a></div>
      <div><a href="https://vardaanglobal.com">www.vardaanglobal.com</a></div>
    </div>
  </div>
  <div class="office-band" contenteditable="true">
    UK &nbsp;|&nbsp; India &nbsp;|&nbsp; USA &nbsp;|&nbsp; Singapore &nbsp;|&nbsp; Australia &nbsp;|&nbsp; UAE
  </div>
  <div style="height: 28px; font-size: 1px; line-height: 1px;" contenteditable="false"></div>
</div>
`;

// ─── Letterhead footer HTML (editable text, non-editable rules) ───────────
const LETTERHEAD_FOOTER = `
<div class="editor-footer">
  <div style="height: 16px; font-size: 1px; line-height: 1px;" contenteditable="false"></div>
  <div class="footer-confidential" contenteditable="true">
    Confidential &amp; Proprietary &copy; 2026 Vardaan Software Services Limited. All rights reserved.
  </div>
  <div class="spacer-foot" contenteditable="false"></div>
  <div class="rule-clay" contenteditable="false"></div>
  <div class="spacer-sm" contenteditable="false"></div>
  <div class="rule-navy" contenteditable="false"></div>
</div>
`;

// ─── Color helpers for visibility safety ──────────────────────────────────
// Parse a CSS color string into {r, g, b} (0-255). Returns null if unparseable.
function parseColor(str) {
  if (!str) return null;
  str = str.trim().toLowerCase();
  // #hex
  let m = str.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/);
  if (m) {
    let hex = m[1];
    if (hex.length === 3) hex = hex.split('').map((c) => c + c).join('');
    return {
      r: parseInt(hex.slice(0, 2), 16),
      g: parseInt(hex.slice(2, 4), 16),
      b: parseInt(hex.slice(4, 6), 16),
    };
  }
  // rgb(r, g, b) / rgba(r, g, b, a)
  m = str.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  if (m) return { r: +m[1], g: +m[2], b: +m[3] };
  // Named colors — common ones
  const named = {
    black: { r: 0, g: 0, b: 0 }, white: { r: 255, g: 255, b: 255 },
    red: { r: 255, g: 0, b: 0 }, blue: { r: 0, g: 0, b: 255 },
    navy: { r: 0, g: 0, b: 128 }, green: { r: 0, g: 128, b: 0 },
    yellow: { r: 255, g: 255, b: 0 }, orange: { r: 255, g: 165, b: 0 },
    purple: { r: 128, g: 0, b: 128 }, gray: { r: 128, g: 128, b: 128 },
    grey: { r: 128, g: 128, b: 128 }, transparent: { r: 255, g: 255, b: 255 },
  };
  if (named[str]) return named[str];
  return null;
}

// Relative luminance (per WCAG). Returns 0 (darkest) to 1 (lightest).
function luminance(str) {
  const c = parseColor(str);
  if (!c) return 0.5; // unknown → treat as mid
  const [r, g, b] = [c.r, c.g, c.b].map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function isDarkColor(str) { return luminance(str) < 0.3; }
function isLightColor(str) { return luminance(str) > 0.7; }

// ─── Word paste sanitization ──────────────────────────────────────────────
// Removes Word's proprietary junk, border styles (which cause black pipe
// artifacts at line ends), and class/namespace attributes — while keeping
// useful inline styles (bold, italic, color, font-size, text-align, etc.).
function sanitizeWordHtml(html) {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;

  // Strip Word-specific XML elements (unwrap children first).
  const wordTags = tmp.querySelectorAll(
    'o\\:p, v\\:shape, v\\:shapetype, v\\:fill, v\\:path, v\\:stroke, ' +
    'o\\:lock, o\\:ink, o\\:smarttagtype, span[style*="mso-"], div[style*="mso-"]'
  );
  wordTags.forEach((el) => {
    while (el.firstChild) el.parentNode.insertBefore(el.firstChild, el);
    el.remove();
  });

  // Remove <style> blocks Word injects, but first extract color/background
  // rules from classes like MsoAccent / MsoShading so we can re-apply them
  // as inline styles before stripping the classes.
  tmp.querySelectorAll('style').forEach((s) => {
    // Try to map Word class-based shading to inline styles
    try {
      const rules = s.textContent.match(/\.[^{]+\{[^}]+\}/g) || [];
      rules.forEach((rule) => {
        const m = rule.match(/\.([a-zA-Z0-9_-]+)\s*\{([^}]+)\}/);
        if (!m) return;
        const cls = m[1];
        const props = m[2];
        const bg = props.match(/(?:background(?:-color)?|mso-shading)\s*:\s*([^;]+)/i);
        const color = props.match(/(?:^|[^-])color\s*:\s*([^;]+)/i);
        if (bg || color) {
          tmp.querySelectorAll('.' + cls).forEach((el) => {
            const existing = el.getAttribute('style') || '';
            const additions = [];
            if (bg && !/background/i.test(existing)) {
              additions.push('background-color: ' + bg[1].trim());
            }
            if (color && !/(^|[^-])color\s*:/i.test(existing)) {
              additions.push('color: ' + color[1].trim());
            }
            if (additions.length) {
              el.setAttribute('style', existing + '; ' + additions.join('; '));
            }
          });
        }
      });
    } catch (_) {}
    s.remove();
  });

  // Before stripping Word shading elements (v:rect, v:fill, etc.), try to
  // carry their fill color onto the parent as background-color.
  tmp.querySelectorAll('v\\:rect, v\\:roundrect').forEach((rect) => {
    const fill = rect.querySelector('v\\:fill');
    const fillColor = fill?.getAttribute('color') || rect.getAttribute('fillcolor');
    if (fillColor) {
      // Find the parent paragraph/div and add background-color
      let parent = rect.parentElement;
      while (parent && !['P', 'DIV', 'TD', 'H1', 'H2', 'H3', 'H4'].includes(parent.tagName)) {
        parent = parent.parentElement;
      }
      if (parent) {
        const existing = parent.getAttribute('style') || '';
        if (!/background/i.test(existing)) {
          parent.setAttribute('style', existing + '; background-color: ' + fillColor);
        }
      }
    }
  });

  // Clean attributes on all elements.
  tmp.querySelectorAll('*').forEach((el) => {
    el.removeAttribute('class');

    // Convert bgcolor HTML attribute → CSS background-color (Word uses bgcolor on td/div)
    const bgcolor = el.getAttribute('bgcolor');
    if (bgcolor) {
      const existing = el.getAttribute('style') || '';
      if (!/background/i.test(existing)) {
        el.setAttribute('style', existing + '; background-color: ' + bgcolor);
      }
      el.removeAttribute('bgcolor');
    }

    // Convert bordercolor HTML attribute → CSS (Word uses on tables/cells)
    const borderColor = el.getAttribute('bordercolor');
    if (borderColor) {
      el.removeAttribute('bordercolor');
    }

    // Remove border attribute from ALL elements (tables, cells, etc.)
    // Word adds border="1" which causes black vertical lines
    if (el.hasAttribute('border')) {
      el.removeAttribute('border');
      el.setAttribute('border', '0');
    }
    // Remove cellspacing/cellpadding (can cause visual artifacts)
    el.removeAttribute('cellspacing');
    el.removeAttribute('cellpadding');

    Array.from(el.attributes).forEach((attr) => {
      if (attr.name.startsWith('xmlns') || attr.name.startsWith('o:') ||
          attr.name.startsWith('v:') || attr.name.startsWith('w:')) {
        el.removeAttribute(attr.name);
      }
    });
    const style = el.getAttribute('style');
    if (style) {
      const cleaned = style
        .split(';')
        .map((s) => s.trim())
        .filter((s) => {
          if (!s) return false;
          const lower = s.toLowerCase();
          // Remove mso- properties
          if (lower.startsWith('mso-')) return false;
          // Remove ALL border styles from ALL elements (including tables).
          if (lower.startsWith('border')) return false;
          // Remove outline styles (can cause vertical lines)
          if (lower.startsWith('outline')) return false;
          // Remove box-shadow and text-shadow (cause black shadow artifacts)
          if (lower.startsWith('box-shadow')) return false;
          if (lower.startsWith('text-shadow')) return false;
          // Remove Word's special effects
          if (lower.startsWith('mso-')) return false;
          return true;
        })
        .join('; ');
      if (cleaned) el.setAttribute('style', cleaned);
      else el.removeAttribute('style');
    }
  });

  // ── Visibility safety: ensure no invisible text ───────────────────────
  // If an element has a dark background but no explicit text color, add a
  // light color. If it has light text but no background, add a dark background.
  // This prevents the "invisible text" problem when Word's shading is lost.
  tmp.querySelectorAll('p, div, span, h1, h2, h3, h4, h5, h6, td, a, strong, b, em, i').forEach((el) => {
    const style = el.getAttribute('style') || '';
    const bgMatch = style.match(/background(?:-color)?\s*:\s*(#[0-9a-fA-F]{3,8}|rgb[a]?\([^)]+\)|\w+)/i);
    const colorMatch = style.match(/(?:^|[^-])\bcolor\s*:\s*(#[0-9a-fA-F]{3,8}|rgb[a]?\([^)]+\)|\w+)/i);

    if (bgMatch) {
      const bg = bgMatch[1].toLowerCase();
      const isDarkBg = isDarkColor(bg);
      const hasLightText = colorMatch && isLightColor(colorMatch[1]);
      // Dark background but no light text → add light text
      if (isDarkBg && !hasLightText) {
        el.setAttribute('style', style + '; color: #FFFFFF');
      }
      // Light text but no explicit dark background and no color set → shouldn't
      // happen often, but if text is light and bg is light, fix it
      if (!isDarkBg && hasLightText && bgMatch) {
        // Light bg + light text → make text dark
        el.setAttribute('style', style.replace(/color\s*:\s*[^;]+/i, 'color: #2D3748'));
      }
    }
  });

  // Remove comment nodes (conditional comments etc.).
  const walker = document.createTreeWalker(tmp, NodeFilter.SHOW_COMMENT, null);
  const comments = [];
  while (walker.nextNode()) comments.push(walker.currentNode);
  comments.forEach((c) => c.remove());

  return tmp.innerHTML;
}

// ─── Variable chips ───────────────────────────────────────────────────────
const VARIABLES = [
  { label: 'Name', value: '{{recipientName}}' },
  { label: 'Email', value: '{{recipientEmail}}' },
  { label: 'Title', value: '{{recipientTitle}}' },
  { label: 'Location', value: '{{recipientLocation}}' },
  { label: 'Date', value: '{{date}}' },
];

const RichTextBody = ({ value, onChange, placeholder }) => {
  const [mode, setMode] = useState('text'); // 'text' | 'html'
  const [letterhead, setLetterhead] = useState(false); // Vardaan letterhead off by default
  const iframeRef = useRef(null);
  const textareaRef = useRef(null);
  const isInternalChange = useRef(false);
  const iframeReady = useRef(false);
  // Store current listener cleanup so we can re-attach when iframe reloads
  const listenerCleanup = useRef(null);
  // Track letterhead state in a ref so writeToIframe sees the current value
  const letterheadRef = useRef(false);
  letterheadRef.current = letterhead;

  // ── iframe helpers ─────────────────────────────────────────────────────
  const getIframeDoc = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe) return null;
    return iframe.contentDocument || iframe.contentWindow?.document || null;
  }, []);

  const getIframeWindow = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe) return null;
    return iframe.contentWindow;
  }, []);

  // ── Read email-safe HTML from iframe ───────────────────────────────────
  // When letterhead is on, builds a table-based layout with inline styles
  // (not CSS classes) so it renders correctly in email clients and preview.
  const readFromIframe = useCallback(() => {
    const doc = getIframeDoc();
    if (!doc) return '';
    const body = doc.querySelector('.editor-body');
    if (!body) return doc.body ? doc.body.innerHTML : '';

    if (letterheadRef.current) {
      // Read the CURRENT (possibly edited) values directly from the iframe DOM
      const companyNameEl = doc.querySelector('.editor-header .company-name');
      const companyName = companyNameEl ? companyNameEl.textContent : 'Software Services Limited';
      const linksEl = doc.querySelector('.editor-header .links-right');
      const linksHtml = linksEl ? linksEl.innerHTML : '<div><a href="https://vardaanglobal.uk">www.vardaanglobal.uk</a></div><div><a href="https://vardaanglobal.com">www.vardaanglobal.com</a></div>';
      const officeEl = doc.querySelector('.editor-header .office-band');
      const officeText = officeEl ? officeEl.textContent : 'UK | India | USA | Singapore | Australia | UAE';
      const footerEl = doc.querySelector('.editor-footer .footer-confidential');
      const footerText = footerEl ? footerEl.textContent : 'Confidential & Proprietary \u00A9 2026 Vardaan Software Services Limited. All rights reserved.';

      return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" bgcolor="#FFFFFF" style="font-family: Arial, Helvetica, sans-serif;"><tr><td align="center" style="padding: 22px 12px;"><table role="presentation" width="680" cellpadding="0" cellspacing="0" border="0" style="max-width: 680px;">` +
        `<tr><td style="border-top: 6px solid #0E2A4A; font-size: 1px; line-height: 1px;">&nbsp;</td></tr>` +
        `<tr><td height="3" style="font-size: 1px; line-height: 1px;">&nbsp;</td></tr>` +
        `<tr><td style="border-top: 1px solid #B8624A; font-size: 1px; line-height: 1px;">&nbsp;</td></tr>` +
        `<tr><td height="20" style="font-size: 1px; line-height: 1px;">&nbsp;</td></tr>` +
        `<tr><td>` +
          `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr>` +
            `<td valign="top" align="left" style="text-align: left;">` +
              `<img src="https://innoida.utho.io/bucket-8iy6qayq/bulk-mail-logos/images/Vardaanlogo.png" width="160" alt="Vardaan Software Services Limited" style="display: block; border: 0; outline: none; text-decoration: none;" />` +
              `<div style="font-size: 11px; margin-top: 10px; font-weight: bold; letter-spacing: 1.2px; text-transform: uppercase; color: #0E2A4A;">${companyName}</div>` +
            `</td>` +
            `<td valign="top" align="right" style="text-align: right; font-size: 11px; line-height: 1.8; color: #0E2A4A;">` +
              linksHtml +
            `</td>` +
          `</tr></table>` +
        `</td></tr>` +
        `<tr><td height="18" style="font-size: 1px; line-height: 1px;">&nbsp;</td></tr>` +
        `<tr><td style="border-top: 1px solid #C9BFA8; border-bottom: 1px solid #C9BFA8; padding: 10px 0; text-align: center; font-size: 11px; letter-spacing: 0.5px; color: #3E4A5B;">` +
          officeText +
        `</td></tr>` +
        `<tr><td height="28" style="font-size: 1px; line-height: 1px;">&nbsp;</td></tr>` +
        `<tr><td style="background:#FFFFFF;font-family:Arial,Helvetica,sans-serif;font-size:12px;color:#2D3748;line-height:1.7;padding:28px 32px;">` +
          body.innerHTML.replace(/background(-color)?\s*:\s*[^;]+;?/gi, '')
                       .replace(/color\s*:\s*#[0-9a-fA-F]{3,8};?/gi, '') +
        `</td></tr>` +
        `<tr>
          <td align="center" style="padding: 18px 0;">
            <table role="presentation" cellpadding="0" cellspacing="0" border="0">
              <tr>
                <td bgcolor="#B8624A" style="padding: 12px 28px; background-color:#B8624A;">
                  <a href="https://bulk-mail.vardaands.com/api/tracking/click/47cc0708-1357-44be-ad97-0aaacd77401a?url=https%3A%2F%2Foutlook.office.com%2Fbook%2FVardaanUK%40infodriveanalytics.com%2F%3Fismsaljsauthenabled" 
                     style="font-family: Arial, Helvetica, sans-serif;
                            font-size: 12px;
                            font-weight: bold;
                            color: #FFFFFF;
                            text-decoration: none;
                            letter-spacing: 1px;">
                    Book a 15-Minute Call
                  </a>
                </td>
              </tr>
            </table>
          </td>
        </tr>` +
        `<tr><td height="16" style="font-size: 1px; line-height: 1px;">&nbsp;</td></tr>` +
        `<tr><td style="border-top: 1px solid #C9BFA8; padding-top: 12px; font-family: Arial, Helvetica, sans-serif; font-size: 9px; color: #6B6258; line-height: 1.5; text-align: center;">` +
          footerText +
        `</td></tr>` +
        `<tr><td height="12" style="font-size: 1px; line-height: 1px;">&nbsp;</td></tr>` +
        `<tr><td style="border-top: 1px solid #B8624A; font-size: 1px; line-height: 1px;">&nbsp;</td></tr>` +
        `<tr><td height="3" style="font-size: 1px; line-height: 1px;">&nbsp;</td></tr>` +
        `<tr><td style="border-top: 6px solid #0E2A4A; font-size: 1px; line-height: 1px;">&nbsp;</td></tr>` +
      `</table></td></tr></table>`;
    }
    return body.innerHTML;
  }, [getIframeDoc]);

  // ── Text mode: input handler ───────────────────────────────────────────
  const handleInput = useCallback(() => {
    isInternalChange.current = true;
    onChange(readFromIframe());
  }, [onChange, readFromIframe]);

  // ── Text mode: paste sanitization inside iframe ────────────────────────
  const handleIframePaste = useCallback((e) => {
    e.preventDefault();
    const html = e.clipboardData.getData('text/html');
    const text = e.clipboardData.getData('text/plain');
    const doc = getIframeDoc();
    const win = getIframeWindow();
    if (!doc || !win) return;

    const sel = win.getSelection();
    if (!sel || !sel.rangeCount) return;
    const range = sel.getRangeAt(0);
    range.deleteContents();

    if (html) {
      const cleaned = sanitizeWordHtml(html);
      const frag = range.createContextualFragment(cleaned);
      range.insertNode(frag);
      range.collapse(false);
    } else if (text) {
      const lines = text.split(/\r?\n/);
      const frag = doc.createDocumentFragment();
      lines.forEach((line, i) => {
        if (i > 0) frag.appendChild(doc.createElement('br'));
        frag.appendChild(doc.createTextNode(line));
      });
      range.insertNode(frag);
      range.collapse(false);
    }

    sel.removeAllRanges();
    sel.addRange(range);
    handleInput();
  }, [getIframeDoc, getIframeWindow, handleInput]);

  // ── Write HTML into iframe and attach listeners ────────────────────────
  const writeToIframe = useCallback((html) => {
    const doc = getIframeDoc();
    if (!doc) return;

    // Clean up previous listeners if any
    if (listenerCleanup.current) {
      listenerCleanup.current();
      listenerCleanup.current = null;
    }

    const bodyContent = html || '<p><span class="editor-placeholder">' +
      (placeholder || 'Start typing or paste from Word...') + '</span></p>';

    const useLetterhead = letterheadRef.current;
    const headerHtml = useLetterhead ? LETTERHEAD_HEADER : '';
    const footerHtml = useLetterhead ? LETTERHEAD_FOOTER : '';
    const pageClass = useLetterhead ? 'editor-page' : 'editor-page editor-plain';

    doc.open();
    doc.write(
      `<!DOCTYPE html><html><head><meta charset="UTF-8"><style>${IFRAME_CSS}</style></head>` +
      `<body><div class="${pageClass}">` +
      headerHtml +
      `<div class="editor-body" contenteditable="true">${bodyContent}</div>` +
      (useLetterhead ?
      `<div style="text-align:center;margin-top:30px;">
        <a href="https://bulk-mail.vardaands.com/api/tracking/click/47cc0708-1357-44be-ad97-0aaacd77401a?url=https%3A%2F%2Foutlook.office.com%2Fbook%2FVardaanUK%40infodriveanalytics.com%2F%3Fismsaljsauthenabled"
           style="display:inline-block;
           padding:14px 35px;
           background:#B8624A;
           color:#FFFFFF;
           text-decoration:none;
           font-family:Arial,Helvetica,sans-serif;
           font-size:13px;
           font-weight:bold;
           letter-spacing:1px;
           text-transform:uppercase;">
           BOOK A 15-MINUTE CALL
        </a>
      </div>` 
      : '') +
footerHtml +
      `</div></body></html>`
    );
    doc.close();
    doc.designMode = 'on';
    iframeReady.current = true;

    // Focus the editable body area
    const bodyDiv = doc.querySelector('.editor-body');
    if (bodyDiv) {
      bodyDiv.focus();
    }

    // Attach listeners NOW — iframe doc is ready
    doc.addEventListener('paste', handleIframePaste);
    doc.addEventListener('input', handleInput);
    listenerCleanup.current = () => {
      doc.removeEventListener('paste', handleIframePaste);
      doc.removeEventListener('input', handleInput);
    };
  }, [getIframeDoc, handleIframePaste, handleInput, placeholder]);

  // ── Seed iframe on mount ───────────────────────────────────────────────
  useEffect(() => {
    if (mode !== 'text') return;
    const t = setTimeout(() => writeToIframe(value || ''), 80);
    return () => {
      clearTimeout(t);
      if (listenerCleanup.current) {
        listenerCleanup.current();
        listenerCleanup.current = null;
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Sync external value → editor (template load, AI apply) ─────────────
  useEffect(() => {
    if (isInternalChange.current) {
      isInternalChange.current = false;
      return;
    }
    if (mode === 'text' && iframeReady.current) {
      const doc = getIframeDoc();
      if (doc) {
        const bodyDiv = doc.querySelector('.editor-body');
        if (bodyDiv) {
          // If the value contains the letterhead table wrapper, extract just
          // the body cell content so we don't double-render the letterhead.
          let bodyContent = value || '';
          if (bodyContent.includes('Vardaanlogo.png') && bodyContent.includes('<table')) {
            // Parse the table and find the main body cell (the one with
            // line-height: 1.7 that contains the actual email content)
            const tmp = document.createElement('div');
            tmp.innerHTML = bodyContent;
            // The body content is in the <td> with style containing "line-height: 1.7"
            // that's NOT a spacer/rule cell — it's the main content cell
            const cells = tmp.querySelectorAll('td');
            let found = null;
            cells.forEach((td) => {
              const style = td.getAttribute('style') || '';
              if (style.includes('line-height: 1.7') && td.innerHTML.length > 20 && !found) {
                found = td;
              }
            });
            if (found) {
              bodyContent = found.innerHTML;
            }
          }
          bodyDiv.innerHTML = bodyContent;
        }
      }
    } else if (mode === 'html' && textareaRef.current) {
      if (textareaRef.current.value !== value) {
        textareaRef.current.value = value || '';
      }
    }
  }, [value, mode, getIframeDoc]);

  // ── Mode switching ─────────────────────────────────────────────────────
  const switchMode = (newMode) => {
    if (newMode === mode) return;
    if (newMode === 'html') {
      // Text → HTML: read iframe → textarea
      const html = readFromIframe();
      if (textareaRef.current) textareaRef.current.value = html;
    } else if (newMode === 'text') {
      // HTML → Text: write textarea → iframe
      setTimeout(() => {
        const v = textareaRef.current ? textareaRef.current.value : value;
        writeToIframe(v);
      }, 50);
    }
    setMode(newMode);
  };

  // ── Variable insertion ─────────────────────────────────────────────────
  const insertVariable = (varValue) => {
    if (mode === 'text') {
      const doc = getIframeDoc();
      const win = getIframeWindow();
      if (!doc || !win) return;
      win.focus();
      const sel = win.getSelection();
      if (sel && sel.rangeCount) {
        const range = sel.getRangeAt(0);
        range.deleteContents();
        const textNode = doc.createTextNode(varValue);
        range.insertNode(textNode);
        range.setStartAfter(textNode);
        range.setEndAfter(textNode);
        sel.removeAllRanges();
        sel.addRange(range);
      } else {
        // No selection — append to editor body
        const bodyDiv = doc.querySelector('.editor-body');
        if (bodyDiv) bodyDiv.appendChild(doc.createTextNode(varValue));
      }
      handleInput();
    } else if (mode === 'html' && textareaRef.current) {
      const ta = textareaRef.current;
      const start = ta.selectionStart;
      const end = ta.selectionEnd;
      const newText = ta.value.slice(0, start) + varValue + ta.value.slice(end);
      ta.value = newText;
      isInternalChange.current = true;
      onChange(newText);
      setTimeout(() => {
        ta.focus();
        ta.setSelectionRange(start + varValue.length, start + varValue.length);
      }, 10);
    }
  };

  // ── Hyperlink helpers ──────────────────────────────────────────────────
  // Add a hyperlink to the currently selected text/block in the iframe.
  const handleAddLink = () => {
    if (mode === 'text') {
      const doc = getIframeDoc();
      const win = getIframeWindow();
      if (!doc || !win) return;
      win.focus();
      const sel = win.getSelection();
      if (!sel || sel.rangeCount === 0 || sel.isCollapsed) {
        // No selection — ask for both link text and URL
        const text = window.prompt('Enter the link text:', '');
        if (!text) return;
        const url = window.prompt('Enter the URL:', 'https://');
        if (!url || url === 'https://') return;
        // Insert a new <a> element at cursor
        const bodyDiv = doc.querySelector('.editor-body');
        if (bodyDiv) {
          const a = doc.createElement('a');
          a.href = url;
          a.textContent = text;
          a.style.color = '#0E2A4A';
          bodyDiv.appendChild(a);
          bodyDiv.appendChild(doc.createElement('br'));
          handleInput();
        }
        return;
      }
      // Has selection — wrap it in a link
      const url = window.prompt('Enter the URL for the selected text:', 'https://');
      if (!url || url === 'https://') return;
      doc.execCommand('createLink', false, url);
      // Style the created link — find <a> tags in the selection and apply styles
      const range = sel.getRangeAt(0);
      const parentEl = range.commonAncestorContainer;
      const container = parentEl.nodeType === 1 ? parentEl : parentEl.parentElement;
      if (container) {
        container.querySelectorAll('a').forEach((a) => {
          if (sel.containsNode(a, true)) {
            a.style.color = '#0E2A4A';
            a.style.textDecoration = 'underline';
          }
        });
      }
      handleInput();
    } else if (mode === 'html' && textareaRef.current) {
      // HTML mode — wrap selected text in <a> tags
      const ta = textareaRef.current;
      const start = ta.selectionStart;
      const end = ta.selectionEnd;
      const selected = ta.value.slice(start, end);
      const url = window.prompt('Enter the URL:', 'https://');
      if (!url || url === 'https://') return;
      const linkText = selected || window.prompt('Enter the link text:', '') || '';
      const replacement = `<a href="${url}" style="color: #0E2A4A; text-decoration: underline;">${linkText}</a>`;
      const newText = ta.value.slice(0, start) + replacement + ta.value.slice(end);
      ta.value = newText;
      isInternalChange.current = true;
      onChange(newText);
      setTimeout(() => {
        ta.focus();
        ta.setSelectionRange(start + replacement.length, start + replacement.length);
      }, 10);
    }
  };

  // Remove hyperlink from the current selection (unwrap the <a>).
  const handleRemoveLink = () => {
    if (mode === 'text') {
      const doc = getIframeDoc();
      const win = getIframeWindow();
      if (!doc || !win) return;
      win.focus();
      const sel = win.getSelection();
      if (!sel || sel.rangeCount === 0) return;
      // Find the anchor containing the selection
      let node = sel.anchorNode;
      while (node && node.tagName !== 'A') {
        node = node.parentElement;
      }
      if (node && node.tagName === 'A') {
        // Unwrap: replace <a> with its text content
        const parent = node.parentNode;
        while (node.firstChild) parent.insertBefore(node.firstChild, node);
        parent.removeChild(node);
        handleInput();
      } else {
        // Fallback: try execCommand
        doc.execCommand('unlink', false, null);
        handleInput();
      }
    } else if (mode === 'html' && textareaRef.current) {
      // HTML mode — strip <a> tags around selection
      const ta = textareaRef.current;
      const start = ta.selectionStart;
      const end = ta.selectionEnd;
      const selected = ta.value.slice(start, end);
      if (selected) {
        const stripped = selected.replace(/<\/?a[^>]*>/gi, '');
        const newText = ta.value.slice(0, start) + stripped + ta.value.slice(end);
        ta.value = newText;
        isInternalChange.current = true;
        onChange(newText);
      }
    }
  };

  return (
    <>
      {/* Mode toggle + variable chips + link buttons */}
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-xs text-neutral-500 font-medium mr-1">Insert:</span>
          {VARIABLES.map((v) => (
            <button
              key={v.value}
              type="button"
              onClick={() => insertVariable(v.value)}
              className="px-2 py-0.5 bg-primary-50 border border-primary-200 text-primary-700 rounded text-xs font-mono hover:bg-primary-100 transition-colors"
            >
              {v.label}
            </button>
          ))}
          {/* Divider */}
          <span className="w-px h-4 bg-neutral-300 mx-1"></span>
          {/* Link / Unlink buttons */}
          <button
            type="button"
            onClick={handleAddLink}
            className="flex items-center gap-1 px-2 py-0.5 bg-neutral-50 border border-neutral-300 text-neutral-700 rounded text-xs hover:bg-neutral-100 transition-colors"
            title="Add hyperlink to selected text (or insert a new link)"
          >
            <FiLink className="w-3 h-3" /> Link
          </button>
          <button
            type="button"
            onClick={handleRemoveLink}
            className="flex items-center gap-1 px-2 py-0.5 bg-neutral-50 border border-neutral-300 text-neutral-700 rounded text-xs hover:bg-neutral-100 transition-colors"
            title="Remove hyperlink from selected text"
          >
            <FiLink2 className="w-3 h-3" /> Unlink
          </button>
        </div>
        <div className="flex items-center gap-2">
          {/* Letterhead toggle — only relevant in Text mode */}
          {mode === 'text' && (
            <label className="flex items-center gap-1.5 text-xs text-neutral-600 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={letterhead}
                onChange={(e) => {
                  const newVal = e.target.checked;
                  // Extract JUST the body content (not the letterhead) so we
                  // don't re-inject the letterhead table into the body area.
                  const doc = getIframeDoc();
                  let bodyContent = '';
                  if (doc) {
                    const bodyDiv = doc.querySelector('.editor-body');
                    if (bodyDiv) {
                      bodyContent = bodyDiv.innerHTML;
                    } else {
                      // Fallback: if value contains letterhead table, extract body cell
                      bodyContent = value || '';
                      if (bodyContent.includes('Vardaanlogo.png') && bodyContent.includes('<table')) {
                        const tmp = document.createElement('div');
                        tmp.innerHTML = bodyContent;
                        const cells = tmp.querySelectorAll('td');
                        let found = null;
                        cells.forEach((td) => {
                          const style = td.getAttribute('style') || '';
                          if (style.includes('line-height: 1.7') && td.innerHTML.length > 20 && !found) {
                            found = td;
                          }
                        });
                        if (found) bodyContent = found.innerHTML;
                      }
                    }
                  }
                  setLetterhead(newVal);
                  letterheadRef.current = newVal;
                  // Rebuild iframe with/without letterhead, preserving body content
                  setTimeout(() => writeToIframe(bodyContent), 50);
                }}
                className="w-3.5 h-3.5 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
              />
              Vardaan Letterhead
            </label>
          )}
          <div className="flex items-center gap-1 border border-neutral-300 rounded-lg overflow-hidden text-xs">
            <button
              type="button"
              onClick={() => switchMode('text')}
              className={`flex items-center gap-1 px-2.5 py-1 transition-all ${
                mode === 'text' ? 'bg-primary-600 text-white' : 'text-neutral-600 hover:bg-neutral-100'
              }`}
              title="Rich text editor — paste from Word/docs preserves formatting"
            >
              <FiType className="w-3 h-3" /> Text
            </button>
            <button
              type="button"
              onClick={() => switchMode('html')}
              className={`flex items-center gap-1 px-2.5 py-1 transition-all ${
                mode === 'html' ? 'bg-primary-600 text-white' : 'text-neutral-600 hover:bg-neutral-100'
              }`}
              title="Raw HTML source — paste or edit HTML directly"
            >
              <FiCode className="w-3 h-3" /> HTML
            </button>
          </div>
        </div>
      </div>

      {/* Editor area */}
      {mode === 'text' ? (
        <iframe
          ref={iframeRef}
          data-body-input="true"
          title="Email body editor"
          className="w-full bg-white border border-neutral-300 rounded-lg focus:border-primary-500 focus:ring-2 focus:ring-primary-200 focus:outline-none transition-all duration-200"
          style={{ minHeight: '400px', height: '450px' }}
        />
      ) : (
        <textarea
          ref={textareaRef}
          data-body-input="true"
          defaultValue={value || ''}
          onChange={(e) => {
            isInternalChange.current = true;
            onChange(e.target.value);
          }}
          placeholder={placeholder || 'Enter your email content. Use {{recipientName}} and {{recipientEmail}} for dynamic content.'}
          rows="14"
          className="w-full px-4 py-3 bg-white border border-neutral-300 rounded-lg focus:border-primary-500 focus:ring-2 focus:ring-primary-200 focus:outline-none transition-all duration-200 text-neutral-900 placeholder-neutral-400 font-mono text-sm leading-relaxed"
        />
      )}

      <p className="text-xs text-neutral-500 mt-2 flex items-center gap-1">
        <span className="font-semibold">Tip:</span> Use {'{{recipientName}}'} and {'{{recipientEmail}}'} for personalization
      </p>
    </>
  );
};

export default RichTextBody;
